/**
 * @module Column Content
 * @description
 */
import { useEffect, useState } from "react";
import { Pagination, Navigation, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { v4 as uuidv4 } from "uuid";
import styles from "./index.module.css";
import "swiper/css";
import "swiper/css/pagination";

/**
 * @function Component
 * @description
 */
const Component = ({ ...props }) => {
  const { headline, subhead, copy, items, theme, useTiles, copyHeading } =
    props;
  const isOdd = (n) => n % 2 !== 0;
  const itemHeadlineClass = `h5 ${styles.component__itemHeadline}`;

  /**
   * @function initMatchHeight
   * @description Initialize our match height script
   */
  const initMatchHeight = () => {
    import("@tannerhodges/match-height").then((result) => {
      const MatchHeight = result;
      MatchHeight.add("div[data-match-height-tiles]", "tile-content");
    });
  };

  const [instanceId, setInstanceId] = useState();
  useEffect(() => {
    const id = uuidv4();
    setInstanceId(id);
    initMatchHeight();
  }, []);

  return (
    <section
      className={`component ${styles.component} ${theme && `u-theme-${theme}`}`}
    >
      <div className="l-container">
        {headline && (
          <div className={styles.component__header}>
            <h2
              className={`h2 ${styles.component__headline} ${theme && styles[`component__headline__${theme}`]}`}
            >
              {headline}
            </h2>
            <div className={`${styles.component__header_v_align}`}>
              <div>
                {subhead && (
                  <h3 className={`h4 ${styles.component__subhead}`}>
                    {subhead}
                  </h3>
                )}
                {copy && <>{copyHeading ? copy : <p>{copy}</p>}</>}
              </div>
            </div>
          </div>
        )}

        <div
          className={`${styles.component__itemList} ${isOdd(items.length) && styles.component__itemListOdd}`}
        >
          {useTiles && (
            <>
              <Swiper
                className={styles.component__slider}
                modules={[Pagination, Navigation, A11y]}
                autoHeight={false}
                spaceBetween={20}
                slidesPerView={1}
                navigation={{
                  nextEl: `.swiper-button-next--${instanceId}`,
                  prevEl: `.swiper-button-prev--${instanceId}`,
                }}
                pagination={{
                  el: `.swiper-pagination--${instanceId}`,
                  type: "bullets",
                  clickable: true,
                }}
                breakpoints={{
                  576: {
                    initialSlide: 1,
                  },
                  768: {
                    initialSlide: 0,
                    slidesPerView: 2.1,
                    spaceBetween: 13,
                  },
                  1024: {
                    slidesPerView: 3,
                    centeredSlides: false,
                    spaceBetween: 18,
                  },
                }}
              >
                {items.map((item, index) => (
                  <SwiperSlide key={index} className={styles.component__slide}>
                    <div
                      className={`${styles.component__tile} ${theme === "dark" && styles.component__tileLight}`}
                      key={index}
                      data-match-height-tiles="tile-content"
                    >
                      {item.icon && (
                        <span
                          className={`icon icon-${item.icon} ${styles.component__icon} ${theme !== "dark" && styles.component__iconAlt}`}
                        />
                      )}
                      {/* Condition below is to make sure heading level only increases by one See: https://dequeuniversity.com/rules/axe/4.0/heading-order */}
                      {subhead ? (
                        <h4 className={itemHeadlineClass}>{item.headline}</h4>
                      ) : (
                        <h3 className={itemHeadlineClass}>{item.headline}</h3>
                      )}
                      <p>{item.content}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              <div
                className={`${styles.component__navigationContainer} hide-for-medium`}
              >
                {/* Previous Arrow */}
                <div
                  className={`swiper-button-prev--${instanceId} icon icon-chevron-left1 ${styles.component__swiperBtnPrev} ${theme && styles[`component__swiperBtn__${theme}`]} `}
                />
                {/* Pagination Bullets */}
                <div
                  className={`swiper-pagination${theme === "dark" ? "__dark" : ""} swiper-pagination--${instanceId}`}
                />
                {/* Next Arrow */}
                <div
                  className={`swiper-button-next--${instanceId} icon icon-chevron-right1 ${styles.component__swiperBtnNext} ${theme && styles[`component__swiperBtn__${theme}`]}`}
                />
              </div>
            </>
          )}

          {items.map((item, index) => (
            <div
              className={`
                ${styles.component__item}
                ${useTiles && styles.component__itemTile}
                ${useTiles && theme === "dark" && styles.component__tileLight}
              `}
              key={index}
            >
              {item.icon && (
                <span
                  className={`icon icon-${item.icon} ${styles.component__icon} ${theme !== "dark" && useTiles && styles.component__iconAlt}`}
                />
              )}
              <h3 className={`h5 ${styles.component__itemHeadline}`}>
                {item.headline}
              </h3>
              <p>{item.content}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Component;
